import { useCallback, useEffect, useState } from 'react'
import {
  ActionIcon,
  Box,
  Breadcrumbs,
  Group,
  Loader,
  Text,
  Anchor,
} from '@mantine/core'
import { IconFolder } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { JanelaFlutuante } from '@/components/ui/JanelaFlutuante'
import { api } from '@/api/client'
import { FileList } from './FileList'
import { FilePreview } from './FilePreview'
import type { FileItem, FileListResponse, PinnedFileBrowser } from './types'
import { ALLOWED_ROOTS } from './types'

interface FloatingFileBrowserProps {
  win: PinnedFileBrowser
  onClose: (id: string) => void
}

export function FloatingFileBrowser({ win, onClose }: FloatingFileBrowserProps) {
  const [currentPath, setCurrentPath] = useState<string>(ALLOWED_ROOTS[0])
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null)
  const [previewExpanded, setPreviewExpanded] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['ec2-files-pinned', win.tenantId, win.instanceId, currentPath],
    queryFn: async () => {
      const res = await api.get<FileListResponse>(
        `/v1/tenants/${win.tenantId}/inventory/ec2/${win.instanceId}/files`,
        { params: { path: currentPath } },
      )
      return res.data
    },
  })

  const [blockedNames, setBlockedNames] = useState<Set<string>>(new Set())

  useEffect(() => {
    setBlockedNames(new Set())
  }, [currentPath])

  const handleBlocked = useCallback((fileName: string) => {
    setBlockedNames((prev) => new Set(prev).add(fileName))
  }, [])

  const items = (data?.items ?? []).map((item) =>
    blockedNames.has(item.name) ? { ...item, blocked: true } : item,
  )

  const handleNavigate = useCallback((path: string) => {
    setCurrentPath(path)
    setPreviewFile(null)
  }, [])

  const handlePreview = useCallback((item: FileItem) => {
    setPreviewFile(item)
  }, [])

  const handleClosePreview = useCallback(() => {
    setPreviewFile(null)
  }, [])

  const breadcrumbSegments = currentPath.split('/').filter(Boolean)

  return (
    <JanelaFlutuante
      titulo={
        <Group gap={6} wrap="nowrap">
          <IconFolder size={14} color="#8b949e" />
          <Text size="xs" fw={600} c="gray.3" truncate>
            {win.instanceName}
          </Text>
        </Group>
      }
      inicial={{ ...win.position, ...win.size }}
      onFechar={() => onClose(win.id)}
    >
      {/* Content */}
      <Box style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Loader size="sm" color="blue" />
          </Box>
        ) : (
          <>
            {!previewExpanded && (
              <Box style={{ flex: previewFile ? 0.5 : 1, overflow: 'auto' }}>
                <FileList
                  items={items}
                  currentPath={currentPath}
                  onNavigate={handleNavigate}
                  onPreview={handlePreview}
                />
              </Box>
            )}
            {previewFile && (
              <Box
                style={{
                  flex: previewExpanded ? 1 : 0.5,
                  overflow: 'auto',
                  borderTop: previewExpanded ? undefined : '1px solid #30363d',
                }}
              >
                <FilePreview
                  tenantId={win.tenantId}
                  instanceId={win.instanceId}
                  file={previewFile}
                  filePath={`${currentPath}/${previewFile.name}`}
                  onClose={() => { handleClosePreview(); setPreviewExpanded(false) }}
                  onBlocked={handleBlocked}
                  expanded={previewExpanded}
                  onToggleExpand={() => setPreviewExpanded((v) => !v)}
                />
              </Box>
            )}
          </>
        )}
      </Box>

    </JanelaFlutuante>
  )
}
